import { Link } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { useDeletePackage, usePackageList } from './hooks';
import { usePageTitle } from '../../hooks/usePageTitle';

export function PackagesPage() {
  usePageTitle('Bundles');
  const { data: packages, isLoading } = usePackageList();
  const del = useDeletePackage();

  const onDelete = async (id: string, name: string) => {
    if (!confirm(`Delete bundle "${name}"? This can't be undone. Documents already sent are unaffected.`)) return;
    await del.mutateAsync(id);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/documents" className="text-sm text-slate-500 hover:text-sky-600">← Documents</Link>
          <h1 className="text-2xl font-semibold text-slate-800 mt-2">Bundles</h1>
          <p className="text-slate-500 text-sm mt-1">Group templates into named bundles to send as one.</p>
        </div>
        <Link
          to="/documents/packages/new"
          className="bg-sky-600 text-white px-3 py-2 rounded hover:bg-sky-700 text-sm inline-flex items-center gap-2"
        >
          <Plus size={16} /> New bundle
        </Link>
      </div>

      <section className="bg-white border border-slate-200 rounded-xl p-6">
        {isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : !packages || packages.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No bundles yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {packages.map((p) => (
              <li key={p.id} className="py-3 flex items-start gap-2">
                <Link
                  to={`/documents/packages/${p.id}`}
                  className="flex-1 block hover:bg-slate-50 -ml-6 pl-6 py-1 rounded"
                >
                  <div className="font-medium text-slate-800">
                    {p.name}
                    {!p.isActive && <span className="ml-2 text-xs text-slate-500">(inactive)</span>}
                  </div>
                  {p.description && <div className="text-sm text-slate-600 mt-0.5">{p.description}</div>}
                  <div className="text-xs text-slate-500 mt-1">
                    {p.templateCount} template{p.templateCount === 1 ? '' : 's'}
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={() => onDelete(p.id, p.name)}
                  disabled={del.isPending}
                  className="text-slate-400 hover:text-red-600 p-2 disabled:opacity-50"
                  aria-label={`Delete ${p.name}`}
                >
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
